import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Video, Upload, X, Play, Image as ImageIcon, CheckCircle2, AlertCircle, Loader2, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

const VideoUpload = ({ user }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [content, setContent] = useState('');
  
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(''); // idle, uploading, processing, complete, error
  const [uploadId, setUploadId] = useState(null);
  
  // Locale attachment state
  const [showLocaleForm, setShowLocaleForm] = useState(false);
  const [localeData, setLocaleData] = useState(null);
  const [localeForm, setLocaleForm] = useState({
    post_type: 'event',
    address: '',
    latitude: '',
    longitude: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    price: '',
    category: '',
    ticket_url: ''
  });

  // Check if user can create locale posts (Premium Business + Admins + Supremium)
  const canCreateLocale = (user?.is_admin) || 
    (user?.subscription_tier === 'supremium') ||
    ((user?.subscription_tier === 'creator' || user?.subscription_tier === 'pro' || user?.subscription_tier === 'premium') && 
     (user?.is_business || user?.account_type === 'business'));
  
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/mpeg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid video format. Please use MP4, WebM, MOV, or AVI.');
      return;
    }
    
    // Validate file size (500MB max)
    if (file.size > 500 * 1024 * 1024) {
      toast.error('Video too large. Maximum size is 500MB.');
      return;
    }
    
    setSelectedFile(file);
    setVideoPreview(URL.createObjectURL(file));
    setUploadStatus('idle');
    setUploadProgress(0);
  }, []);
  
  const handleThumbnailSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid image format. Please use JPEG, PNG, WebP, or GIF.');
      return;
    }
    
    setThumbnail(file);
    setThumbnailPreview(URL.createObjectURL(file));
  }, []);
  
  const uploadChunked = async () => {
    if (!selectedFile) {
      toast.error('Please select a video file');
      return;
    }
    
    setUploading(true);
    setUploadStatus('uploading');
    setUploadProgress(0);
    
    try {
      const totalChunks = Math.ceil(selectedFile.size / CHUNK_SIZE);
      
      // Initialize upload
      const initResponse = await axios.post(
        `${API_URL}/video-upload/init-chunked`,
        {
          filename: selectedFile.name,
          file_size: selectedFile.size,
          content_type: selectedFile.type,
          total_chunks: totalChunks
        },
        { withCredentials: true }
      );
      
      const currentUploadId = initResponse.data.upload_id;
      setUploadId(currentUploadId);
      
      // Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, selectedFile.size);
        const chunk = selectedFile.slice(start, end);
        
        const formData = new FormData();
        formData.append('chunk', chunk);
        
        await axios.post(
          `${API_URL}/video-upload/chunk/${currentUploadId}/${i}`,
          formData,
          {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        
        setUploadProgress(((i + 1) / totalChunks) * 90); // Save 10% for processing
      }
      
      // Upload thumbnail if provided (using regular upload)
      let thumbnailUrl = null;
      if (thumbnail) {
        // For simplicity, upload thumbnail as base64 or use a separate endpoint
        // Here we'll skip thumbnail for chunked uploads
      }
      
      setUploadStatus('processing');
      
      // Complete upload
      const completeResponse = await axios.post(
        `${API_URL}/video-upload/complete-chunked`,
        {
          upload_id: currentUploadId,
          content: content || 'Check out my new video!',
          thumbnail_url: thumbnailUrl
        },
        { withCredentials: true }
      );
      
      setUploadProgress(100);
      setUploadStatus('complete');
      
      // If locale data is attached, create a locale post too
      if (localeData && canCreateLocale) {
        try {
          await axios.post(
            `${API_URL}/locale/`,
            {
              title: content.substring(0, 100) || 'Video Event',
              description: content || 'Check out my new video!',
              post_type: localeData.post_type,
              address: localeData.address,
              latitude: parseFloat(localeData.latitude) || 40.7128,
              longitude: parseFloat(localeData.longitude) || -74.0060,
              start_date: localeData.start_date || null,
              end_date: localeData.end_date || null,
              start_time: localeData.start_time || null,
              end_time: localeData.end_time || null,
              price: localeData.price || null,
              category: localeData.category || null,
              ticket_url: localeData.ticket_url || null,
              image_url: thumbnailPreview || null,
              tags: [],
              linked_post_id: completeResponse.data?.post_id
            },
            { withCredentials: true }
          );
          toast.success('Video uploaded with Locale!');
        } catch (localeError) {
          console.error('Failed to create locale entry');
          toast.success('Video uploaded! (Locale link failed)');
        }
      } else {
        toast.success('Video uploaded successfully!');
      }
      
      // Redirect to feed after delay
      setTimeout(() => {
        navigate('/feed');
      }, 2000);
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      toast.error(error.response?.data?.detail || 'Upload failed. Please try again.');
      
      // Cancel upload if in progress
      if (uploadId) {
        try {
          await axios.delete(`${API_URL}/video-upload/cancel/${uploadId}`, { withCredentials: true });
        } catch (e) {}
      }
    } finally {
      setUploading(false);
    }
  };
  
  const cancelUpload = async () => {
    if (uploadId) {
      try {
        await axios.delete(`${API_URL}/video-upload/cancel/${uploadId}`, { withCredentials: true });
        toast.info('Upload cancelled');
      } catch (e) {}
    }
    setUploading(false);
    setUploadStatus('idle');
    setUploadProgress(0);
    setUploadId(null);
  };
  
  const clearSelection = () => {
    setSelectedFile(null);
    setVideoPreview(null);
    setThumbnail(null);
    setThumbnailPreview(null);
    setUploadStatus('idle');
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
  };
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-32" data-testid="video-upload-page">
      <div className="max-w-3xl mx-auto px-6 pt-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center">
              <Video className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-4xl font-heading font-black text-gray-900 dark:text-white">Upload Video</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">Share your videos with the world</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="p-8 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
            {/* File Selection Area */}
            {!selectedFile ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-3 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-12 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
                data-testid="video-dropzone"
              >
                <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Click to select a video
                </p>
                <p className="text-gray-500 dark:text-gray-400">
                  MP4, WebM, MOV, AVI • Max 500MB
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg"
                  onChange={handleFileSelect}
                  className="sr-only"
                  data-testid="video-input"
                />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Video Preview */}
                <div className="relative rounded-2xl overflow-hidden bg-black">
                  <video
                    src={videoPreview}
                    className="w-full max-h-80 object-contain"
                    controls
                    data-testid="video-preview"
                  />
                  {!uploading && (
                    <button
                      onClick={clearSelection}
                      className="absolute top-3 right-3 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                      data-testid="clear-video-btn"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                
                {/* File Info */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Video className="w-8 h-8 text-red-500" />
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white truncate max-w-xs">
                        {selectedFile.name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {uploadStatus === 'complete' && (
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    )}
                    {uploadStatus === 'error' && (
                      <AlertCircle className="w-6 h-6 text-red-500" />
                    )}
                  </div>
                </div>
                
                {/* Upload Progress */}
                <AnimatePresence>
                  {uploading && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {uploadStatus === 'processing' ? 'Processing video...' : 'Uploading...'}
                        </span>
                        <span className="text-sm text-gray-500">
                          {uploadProgress.toFixed(0)}%
                        </span>
                      </div>
                      <Progress value={uploadProgress} className="h-3" />
                      <Button
                        onClick={cancelUpload}
                        variant="outline"
                        className="w-full rounded-xl"
                        disabled={uploadStatus === 'processing'}
                      >
                        Cancel Upload
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Thumbnail Upload */}
                {!uploading && uploadStatus !== 'complete' && (
                  <div>
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
                      Thumbnail (Optional)
                    </label>
                    <div className="flex items-center gap-4">
                      {thumbnailPreview ? (
                        <div className="relative w-32 h-20 rounded-xl overflow-hidden">
                          <img src={thumbnailPreview} alt="Thumbnail" className="w-full h-full object-cover" />
                          <button
                            onClick={() => {
                              setThumbnail(null);
                              setThumbnailPreview(null);
                              if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
                            }}
                            className="absolute top-1 right-1 p-1 bg-black/50 rounded-full text-white"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => thumbnailInputRef.current?.click()}
                          className="w-32 h-20 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl flex flex-col items-center justify-center hover:border-primary transition-colors"
                        >
                          <ImageIcon className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-500 mt-1">Add thumbnail</span>
                        </button>
                      )}
                      <input
                        ref={thumbnailInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleThumbnailSelect}
                        className="sr-only"
                      />
                    </div>
                  </div>
                )}
                
                {/* Description */}
                {!uploading && uploadStatus !== 'complete' && (
                  <div>
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
                      Description
                    </label>
                    <Textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="What's this video about?"
                      className="rounded-xl resize-none"
                      rows={3}
                      data-testid="video-description"
                    />
                  </div>
                )}
                
                {/* Attach Locale Section */}
                {!uploading && uploadStatus !== 'complete' && canCreateLocale && (
                  <div>
                    {/* Locale Attachment Badge */}
                    {localeData && (
                      <div className="mb-3 p-3 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-orange-500" />
                            <div>
                              <span className="text-sm font-semibold text-orange-700 dark:text-orange-300">
                                {localeData.post_type.charAt(0).toUpperCase() + localeData.post_type.slice(1)} at {localeData.address}
                              </span>
                              {localeData.start_date && (
                                <p className="text-xs text-orange-600 dark:text-orange-400">
                                  {localeData.start_date} {localeData.start_time && `at ${localeData.start_time}`}
                                </p>
                              )}
                            </div>
                          </div>
                          <button 
                            onClick={() => { setLocaleData(null); setShowLocaleForm(false); }}
                            className="p-1 hover:bg-orange-200 dark:hover:bg-orange-800 rounded-full"
                          >
                            <X className="w-4 h-4 text-orange-500" />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Locale Form */}
                    <AnimatePresence>
                      {showLocaleForm && !localeData && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="mb-3 p-4 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 rounded-xl border border-orange-200 dark:border-orange-800 overflow-hidden"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-orange-700 dark:text-orange-300 flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              Add Locale Details
                            </h4>
                            <button onClick={() => setShowLocaleForm(false)} className="text-gray-500 hover:text-gray-700">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                            {['event', 'show', 'special', 'deal'].map(type => (
                              <button
                                key={type}
                                onClick={() => setLocaleForm({ ...localeForm, post_type: type })}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                  localeForm.post_type === type 
                                    ? type === 'event' ? 'bg-blue-500 text-white' 
                                      : type === 'show' ? 'bg-purple-500 text-white'
                                      : type === 'special' ? 'bg-orange-500 text-white'
                                      : 'bg-green-500 text-white'
                                    : 'bg-white dark:bg-gray-700 border'
                                }`}
                              >
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                              </button>
                            ))}
                          </div>
                          
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={localeForm.address}
                              onChange={(e) => setLocaleForm({ ...localeForm, address: e.target.value })}
                              placeholder="Address *"
                              className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                            />
                            
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="number"
                                step="0.0001"
                                value={localeForm.latitude}
                                onChange={(e) => setLocaleForm({ ...localeForm, latitude: e.target.value })}
                                placeholder="Latitude"
                                className="px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                              />
                              <input
                                type="number"
                                step="0.0001"
                                value={localeForm.longitude}
                                onChange={(e) => setLocaleForm({ ...localeForm, longitude: e.target.value })}
                                placeholder="Longitude"
                                className="px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="date"
                                value={localeForm.start_date}
                                onChange={(e) => setLocaleForm({ ...localeForm, start_date: e.target.value })}
                                className="px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                              />
                              <input
                                type="time"
                                value={localeForm.start_time}
                                onChange={(e) => setLocaleForm({ ...localeForm, start_time: e.target.value })}
                                className="px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="text"
                                value={localeForm.price}
                                onChange={(e) => setLocaleForm({ ...localeForm, price: e.target.value })}
                                placeholder="Price (Free, $10, etc.)"
                                className="px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                              />
                              <select
                                value={localeForm.category}
                                onChange={(e) => setLocaleForm({ ...localeForm, category: e.target.value })}
                                className="px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                              >
                                <option value="">Category</option>
                                <option value="music">Music</option>
                                <option value="food">Food & Dining</option>
                                <option value="sports">Sports</option>
                                <option value="art">Art & Culture</option>
                                <option value="nightlife">Nightlife</option>
                                <option value="shopping">Shopping</option>
                                <option value="fitness">Fitness</option>
                                <option value="tech">Tech</option>
                                <option value="community">Community</option>
                              </select>
                            </div>
                            
                            <input
                              type="url"
                              value={localeForm.ticket_url}
                              onChange={(e) => setLocaleForm({ ...localeForm, ticket_url: e.target.value })}
                              placeholder="Ticket/Booking URL (optional)"
                              className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                            />
                          </div>
                          
                          <div className="flex justify-end gap-2 mt-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowLocaleForm(false)}
                              className="rounded-full"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                if (!localeForm.address) {
                                  toast.error('Please enter an address');
                                  return;
                                }
                                setLocaleData({ ...localeForm });
                                setShowLocaleForm(false);
                              }}
                              className="rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-white"
                            >
                              <MapPin className="w-3 h-3 mr-1" />
                              Attach Location
                            </Button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    
                    {/* Attach Locale Button */}
                    {!localeData && !showLocaleForm && (
                      <Button
                        variant="outline"
                        onClick={() => setShowLocaleForm(true)}
                        className="w-full rounded-xl border-dashed border-2 border-orange-300 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                        data-testid="video-attach-locale-btn"
                      >
                        <MapPin className="w-4 h-4 mr-2" />
                        Attach Locale
                      </Button>
                    )}
                  </div>
                )}
                
                {/* Upload Button */}
                {!uploading && uploadStatus !== 'complete' && (
                  <Button
                    onClick={uploadChunked}
                    className="w-full h-14 rounded-2xl bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-bold text-lg shadow-lg"
                    data-testid="upload-video-btn"
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    Upload Video
                  </Button>
                )}
                
                {/* Success State */}
                {uploadStatus === 'complete' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-6"
                  >
                    <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Upload Complete!
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Redirecting to feed...
                    </p>
                  </motion.div>
                )}
              </div>
            )}
          </Card>
          
          {/* Tips */}
          <Card className="mt-6 p-6 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3">Upload Tips</h3>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                Use MP4 format for best compatibility
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                Keep videos under 500MB for faster uploads
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                Add a thumbnail to make your video stand out
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                Write a descriptive caption to help others find your content
              </li>
            </ul>
          </Card>
        </motion.div>
      </div>
      
      <FloatingDock user={user} />
    </div>
  );
};

export default VideoUpload;
